import React from 'react';
import { ButtonWithLoading } from '@spark/ui-components/components/Button';
import Icon from '@spark/ui-components/components/Icon';
import TableBadge from '@spark/ui-components/components/TanStackTable/components/TableBadge';
import Tooltip from '@spark/ui-components/components/Tooltip';
import { formatarCNPJ } from '@spark/utils/formatters';
import { formatarDataComHora } from '../../shared/controleDescargaMapper';
import { DIAS_CURTOS, TEXTO_MODO, mascararRaiz, paraRascunho, textoDia } from '../utils/janelaDescarga';

const subtituloNome = (janela) => {
  if (janela.tipo === 'CNPJ') {
    return `Exceção da raiz ${mascararRaiz(janela.cnpjRaiz)}`;
  }

  if (janela.quantidadeCnpjs === null || janela.quantidadeCnpjs === undefined) return null;

  const excecoes = janela.quantidadeExcecoes ?? 0;
  const seguem = Math.max(0, janela.quantidadeCnpjs - excecoes);
  return `${seguem} CNPJs seguem esta janela · ${excecoes} com janela própria`;
};

const BotaoAcao = ({ texto, variant, icone, onClick, loading }) => (
  <Tooltip dir="top" text={texto}>
    <span className="d-inline-block">
      <ButtonWithLoading
        variant={variant}
        size="sm"
        aria-label={texto}
        loading={loading}
        disabled={loading}
        loadingText=""
        style={{ minWidth: 'auto' }}
        onClick={(event) => {
          event.stopPropagation();
          onClick();
        }}
      >
        <Icon name={icone} size={14} />
      </ButtonWithLoading>
    </span>
  </Tooltip>
);

export const getJanelasColumns = ({
  podeEditar = false,
  onEditar,
  onHistorico,
  onExcluir,
  loadingAcao
} = {}) => {
  const emAcao = (janela, acao) => loadingAcao?.id === janela.id && loadingAcao?.acao === acao;

  const columns = [
    {
      id: 'nome',
      header: 'Janela',
      accessorKey: 'nome',
      size: 260,
      enableSorting: false,
      cell: ({ row }) => {
        const subtitulo = subtituloNome(row.original);
        return (
          <div>
            <div className="fw-semibold">{row.original.nome}</div>
            {subtitulo ? <small className="text-muted">{subtitulo}</small> : null}
          </div>
        );
      }
    },
    {
      id: 'aplicaSeA',
      header: 'Aplica-se a',
      accessorKey: 'tipo',
      size: 190,
      enableSorting: false,
      cell: ({ row }) => {
        const janela = row.original;
        const porCnpj = janela.tipo === 'CNPJ';
        return (
          <div className="d-flex flex-column align-items-start" style={{ gap: 4 }}>
            <TableBadge label={porCnpj ? 'CNPJ individual' : 'Raiz do CNPJ'} variant="default" />
            <span>{porCnpj ? formatarCNPJ(janela.cnpj) : mascararRaiz(janela.cnpjRaiz)}</span>
          </div>
        );
      }
    },
    ...DIAS_CURTOS.map((diaCurto, indice) => ({
      id: `dia${indice + 1}`,
      header: diaCurto,
      size: 120,
      enableSorting: false,
      accessorFn: (janela) => textoDia(paraRascunho(janela).dias[indice]),
      cell: ({ getValue }) => {
        const texto = getValue();
        return texto === 'Fechado' ? <span className="text-muted">{texto}</span> : <span>{texto}</span>;
      }
    })),
    {
      id: 'foraJanela',
      header: 'Fora da janela',
      accessorKey: 'modoForaJanela',
      size: 150,
      enableSorting: false,
      cell: ({ getValue }) => TEXTO_MODO[getValue()] ?? getValue() ?? '-'
    },
    {
      id: 'ultimaAlteracao',
      header: 'Última alteração',
      accessorKey: 'dataAtualizacao',
      size: 180,
      enableSorting: false,
      cell: ({ row }) => (
        <div>
          <div>{formatarDataComHora(row.original.dataAtualizacao)}</div>
          {row.original.usuarioAtualizacao ? (
            <small className="text-muted">{row.original.usuarioAtualizacao}</small>
          ) : null}
        </div>
      )
    }
  ];

  columns.unshift({
    id: 'acoes',
    header: 'Ações',
    accessorKey: 'id',
    size: podeEditar ? 150 : 70,
    enableHiding: false,
    enablePinning: true,
    enableSorting: false,
    enableCopy: false,
    cell: ({ row }) => (
      <div className="d-inline-flex align-items-center" style={{ gap: 6 }}>
        {podeEditar && onEditar ? (
          <BotaoAcao
            texto="Editar janela"
            variant="outline-primary"
            icone="pencil"
            loading={emAcao(row.original, 'editar')}
            onClick={() => onEditar(row.original)}
          />
        ) : null}
        {onHistorico ? (
          <BotaoAcao
            texto="Histórico da janela"
            variant="outline-info"
            icone="file-document-outline"
            loading={emAcao(row.original, 'historico')}
            onClick={() => onHistorico(row.original)}
          />
        ) : null}
        {podeEditar && onExcluir ? (
          <BotaoAcao
            texto="Excluir janela"
            variant="outline-danger"
            icone="delete"
            loading={emAcao(row.original, 'excluir')}
            onClick={() => onExcluir(row.original)}
          />
        ) : null}
      </div>
    )
  });

  return columns;
};
